import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext();

const WEBSOCKET_TICKET_URL =
  'https://3mv40ugihi.execute-api.eu-west-2.amazonaws.com/dev/websocket-ticket';

const WEBSOCKET_URL =
  'wss://z6nq21w1yg.execute-api.eu-west-2.amazonaws.com/dev';

const BOOKINGS_URL =
  'https://gp64xgt52i.execute-api.eu-west-2.amazonaws.com/dev/bookings';

export const WebSocketProvider = ({ children }) => {
  const {
    isAuthenticated,
    authInitialized,
    accountReady,
    userAttributes,
    getAccessToken,
  } = useAuth();

  const webSocketRef = useRef(null);
  const shouldReconnectRef = useRef(true);
  const heartbeatIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const connectionAttemptRef = useRef(0);

  const [
    openChats,
    setOpenChats,
  ] = useState(() => {
    try {
      return JSON.parse(
        localStorage.getItem('openChats')
      ) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (
      authInitialized &&
      !isAuthenticated
    ) {
      setOpenChats([]);
    }
  }, [
    authInitialized,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (!authInitialized) {
      return;
    }

    localStorage.setItem(
      'openChats',
      JSON.stringify(openChats)
    );
  }, [
    authInitialized,
    openChats,
  ]);

  const startHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(
        heartbeatIntervalRef.current
      );
    }

    heartbeatIntervalRef.current =
      setInterval(() => {
        if (
          webSocketRef.current &&
          webSocketRef.current.readyState ===
            WebSocket.OPEN
        ) {
          console.log(
            '[WebSocketContext] Sending ping to server (heartbeat)'
          );

          webSocketRef.current.send(
            JSON.stringify({
              type: 'ping',
            })
          );
        }
      }, 540000);
  };

  const refreshBookingById = useCallback(async (bookingId) => {
    if (!bookingId) {
      return null;
    }

    const jwt = await getAccessToken();
    const response = await fetch(
      `${BOOKINGS_URL}/${encodeURIComponent(bookingId)}`,
      {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    const payload = await response
      .json()
      .catch(() => ({}));

    if (!response.ok || !payload.booking) {
      throw new Error(
        payload?.error ||
        `Failed to refresh booking (HTTP ${response.status})`
      );
    }

    const booking = payload.booking;

    console.log(
      '[WebSocketContext] Refreshing authoritative booking state:',
      bookingId
    );

    setOpenChats((prevChats) =>
      (Array.isArray(prevChats) ? prevChats : []).map((chat) => ({
        ...chat,
        messages: (chat.messages ?? []).map((item) =>
          item.isBooking &&
          item.bookingDetails?.id === booking.id
            ? {
                ...item,
                bookingDetails: booking,
              }
            : item
        ),
      }))
    );

    return booking;
  }, [getAccessToken]);

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(
        heartbeatIntervalRef.current
      );

      heartbeatIntervalRef.current = null;
    }
  };

  const handleIncomingMessage = async (event) => {
    try {
      console.log(
        '[WebSocketContext] Received WebSocket message:',
        event.data
      );

      const data = JSON.parse(event.data);

      const {
        action,
        senderUserSub,
        senderName,
        message,
        recipientUserSub,
        bookingId,
      } = data;

      const mySub =
        localStorage.getItem('userSub');

      console.log(
        '[WebSocketContext] My sub:',
        mySub,
        '| Incoming recipientSub:',
        recipientUserSub,
        '| Action:',
        action
      );

      if (!mySub) {
        console.warn(
          '[WebSocketContext] mySub is undefined, ignoring inbound message to avoid duplicates'
        );
        return;
      }

      if (
        action === 'bookingUpdated' &&
        bookingId
      ) {
        console.log(
          '[WebSocketContext] Received bookingUpdated:',
          bookingId,
          'for',
          recipientUserSub
        );
        if (
          recipientUserSub &&
          recipientUserSub !== mySub
        ) {
          return;
        }

        const refreshedBooking =
          await refreshBookingById(bookingId);

        console.log(
          '[WebSocketContext] Refreshed booking from bookingUpdated:',
          refreshedBooking
        );
        return;
      }

      if (senderUserSub === mySub) {
        console.warn(
          '[WebSocketContext] Inbound message where sender === me. Possibly echo? Skipping...'
        );
        return;
      }

      if (recipientUserSub !== mySub) {
        return;
      }

      if (action === 'sendMessage') {
        setOpenChats((prevChats) => {
          const chats = Array.isArray(prevChats)
            ? prevChats
            : [];

          const existingChat =
            chats.find(
              (chat) =>
                chat.recipientUserSub ===
                senderUserSub
            );

          const incomingMessage = {
            text: message,
            sent: false,
          };

          if (existingChat) {
            return chats.map(
              (chat) =>
                chat.recipientUserSub ===
                senderUserSub
                  ? {
                      ...chat,
                      chatPartnerName:
                        chat.chatPartnerName ||
                        senderName,
                      messages: [
                        ...(chat.messages ?? []),
                        incomingMessage,
                      ],
                    }
                  : chat
            );
          }

          return [
            ...chats,
            {
              recipientUserSub:
                senderUserSub,
              chatPartnerName:
                senderName,
              messages: [
                incomingMessage,
              ],
              unreadCount: 0,
            },
          ];
        });

        window.dispatchEvent(
          new CustomEvent(
            'messageReceived',
            {
              detail: {
                senderUserSub,
                message,
                senderName,
                kind: 'message',
              },
            }
          )
        );

        return;
      }

      if (
        action === 'sendBooking' &&
        bookingId
      ) {
        const jwt = await getAccessToken();

        const response = await fetch(
          `${BOOKINGS_URL}/${encodeURIComponent(bookingId)}`,
          {
            headers: {
              Authorization:
                `Bearer ${jwt}`,
            },
          }
        );

        const payload = await response
          .json()
          .catch(() => ({}));

        if (!response.ok || !payload.booking) {
          throw new Error(
            payload?.error ||
            `Failed to load booking (HTTP ${response.status})`
          );
        }

        const bookingDetails =
          payload.booking;

        const currentChats = Array.isArray(openChats)
          ? openChats
          : [];
        const currentChat = currentChats.find(
          (chat) =>
            chat.recipientUserSub === senderUserSub
        );

        console.log(
          '[WebSocketContext] Reconciling sendBooking:',
          {
            incomingBookingId: bookingDetails.id,
            senderUserSub,
            existingBookingIds:
              currentChat?.messages
                ?.filter(
                  (item) =>
                    item.isBooking &&
                    item.bookingDetails?.id
                )
                .map(
                  (item) =>
                    item.bookingDetails.id
                ) || [],
          }
        );

        const bookingMessage =
          `Booking request for ${bookingDetails.eventName} ` +
          `at £${bookingDetails.price}`;

        setOpenChats((prevChats) => {
          const chats = Array.isArray(prevChats)
            ? prevChats
            : [];

          const makeMsgObj = () => ({
            text: bookingMessage,
            sent: false,
            isBooking: true,
            bookingDetails,
          });

          const existingChat =
            chats.find(
              (chat) =>
                chat.recipientUserSub ===
                senderUserSub
            );

          if (existingChat) {
            return chats.map((chat) => {
              if (
                chat.recipientUserSub !==
                senderUserSub
              ) {
                return chat;
              }

              const messages =
                chat.messages ?? [];
              const existingBookingIndex =
                messages.findIndex(
                  (item) =>
                    item.isBooking &&
                    item.bookingDetails?.id ===
                      bookingDetails.id
                );

              console.log(
                '[WebSocketContext] sendBooking existing index:',
                existingBookingIndex
              );

              if (existingBookingIndex >= 0) {
                return {
                  ...chat,
                  chatPartnerName:
                    chat.chatPartnerName ||
                    senderName,
                  messages: messages.map(
                    (item, index) =>
                      index === existingBookingIndex
                        ? {
                            ...item,
                            bookingDetails,
                          }
                        : item
                  ),
                };
              }

              return {
                ...chat,
                chatPartnerName:
                  chat.chatPartnerName ||
                  senderName,
                messages: [
                  ...messages,
                  makeMsgObj(),
                ],
              };
            });
          }

          return [
            ...chats,
            {
              recipientUserSub:
                senderUserSub,
              chatPartnerName:
                senderName,
              messages: [
                makeMsgObj(),
              ],
              unreadCount: 0,
            },
          ];
        });

        window.dispatchEvent(
          new CustomEvent(
            'messageReceived',
            {
              detail: {
                senderUserSub,
                senderName,
                kind: 'booking',
                bookingId,
              },
            }
          )
        );
      }
    } catch (error) {
      console.error(
        '[WebSocketContext] Error processing incoming message:',
        error
      );
    }
  };

  const getWebSocketTicket = async () => {
    const accessToken =
      await getAccessToken();

    const response = await fetch(
      WEBSOCKET_TICKET_URL,
      {
        method: 'POST',
        headers: {
          Authorization:
            `Bearer ${accessToken}`,
        },
      }
    );

    const body =
      await response
        .json()
        .catch(() => ({}));

    if (!response.ok || !body.ticket) {
      throw new Error(
        body?.error ||
        `Failed to obtain WebSocket ticket (HTTP ${response.status})`
      );
    }

    return body.ticket;
  };

  const connectWebSocket = async (
    userSub
  ) => {
    console.log(
      '[WebSocketContext] Attempting authenticated WebSocket connection'
    );

    shouldReconnectRef.current = true;

    if (
      webSocketRef.current &&
      webSocketRef.current.readyState !==
        WebSocket.CLOSED
    ) {
      console.log(
        '[WebSocketContext] WebSocket is already open or opening; skipping'
      );
      return;
    }

    const attemptId =
      ++connectionAttemptRef.current;

    try {
      const ticket =
        await getWebSocketTicket();

      if (
        attemptId !==
          connectionAttemptRef.current ||
        !shouldReconnectRef.current
      ) {
        console.log(
          '[WebSocketContext] Ignoring stale WebSocket connection attempt'
        );
        return;
      }

      if (
        webSocketRef.current &&
        webSocketRef.current.readyState !==
          WebSocket.CLOSED
      ) {
        console.log(
          '[WebSocketContext] WebSocket opened while ticket was being fetched; skipping duplicate connection'
        );
        return;
      }

      const wsUrl =
        `${WEBSOCKET_URL}?ticket=` +
        encodeURIComponent(ticket);

      const socket =
        new WebSocket(wsUrl);

      webSocketRef.current = socket;

      socket.onopen =
        () => {
          if (
            webSocketRef.current !==
            socket
          ) {
            socket.close();
            return;
          }

          console.log(
            '[WebSocketContext] WebSocket connection established'
          );

          reconnectAttemptsRef.current = 0;

          shouldReconnectRef.current =
            true;

          startHeartbeat();
        };

      socket.onclose =
        (event) => {
          console.log(
            '[WebSocketContext] WebSocket disconnected.',
            {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean,
              shouldReconnect:
                shouldReconnectRef.current,
            }
          );

          if (
            webSocketRef.current !==
            socket
          ) {
            return;
          }

          stopHeartbeat();
          webSocketRef.current = null;

          if (
            shouldReconnectRef.current
          ) {
            handleWebSocketReconnect(
              userSub
            );
          }
        };

      socket.onmessage =
        handleIncomingMessage;
    } catch (error) {
      console.error(
        '[WebSocketContext] Failed to authenticate WebSocket connection:',
        error
      );

      if (
        shouldReconnectRef.current
      ) {
        handleWebSocketReconnect(
          userSub
        );
      }
    }
  };

  const handleWebSocketReconnect = (
    userSub
  ) => {
    const nextAttempt =
      reconnectAttemptsRef.current + 1;

    if (nextAttempt > 5) {
      console.log(
        '[WebSocketContext] Max reconnection attempts reached'
      );
      return;
    }

    const retryDelay = Math.min(
      1000 * 2 ** (nextAttempt - 1),
      30000
    );

    if (reconnectTimeoutRef.current) {
      clearTimeout(
        reconnectTimeoutRef.current
      );
    }

    reconnectTimeoutRef.current =
      setTimeout(() => {
        reconnectTimeoutRef.current = null;

        if (!shouldReconnectRef.current) {
          return;
        }

        reconnectAttemptsRef.current =
          nextAttempt;

        console.log(
          `[WebSocketContext] Reconnecting... attempt ${nextAttempt}`
        );

        connectWebSocket(userSub);
      }, retryDelay);
  };

  const disconnectWebSocket = () => {
    shouldReconnectRef.current = false;
    connectionAttemptRef.current += 1;

    if (reconnectTimeoutRef.current) {
      clearTimeout(
        reconnectTimeoutRef.current
      );
      reconnectTimeoutRef.current = null;
    }

    reconnectAttemptsRef.current = 0;
    stopHeartbeat();

    if (webSocketRef.current) {
      console.log(
        '[WebSocketContext] Closing WebSocket intentionally'
      );

      webSocketRef.current.onclose =
        () =>
          console.log(
            '[WebSocketContext] WebSocket intentionally disconnected'
          );

      webSocketRef.current.close();
      webSocketRef.current = null;
    }
  };

  useEffect(() => {
    const userSub =
      userAttributes?.sub ||
      localStorage.getItem(
        'userSub'
      );

    if (
      isAuthenticated &&
      accountReady !== false &&
      userSub
    ) {
      connectWebSocket(userSub);
    } else {
      disconnectWebSocket();
    }

    return () => {
      disconnectWebSocket();
    };
  }, [
    isAuthenticated,
    userAttributes?.sub,
    accountReady,
  ]);

  return (
    <WebSocketContext.Provider
      value={{
        connectWebSocket,
        disconnectWebSocket,
        webSocketRef,
        openChats,
        setOpenChats,
        refreshBookingById,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () =>
  useContext(WebSocketContext);